/**
 * Module Permissions Configuration
 *
 * Defines which user roles have access to which modules
 */

export type ModuleName =
  | 'on-model' | 'flat-lay' | 'easy-way'
  | 'stylista' | 'dressing-room' | 'makeup' | 'hairdresser'
  | 'video' | 'credits' | 'admin' | 'photo-tools';
export type UserRole = 'user' | 'b2b-user' | 'admin' | 'mobile-users';

/**
 * Module access matrix — SSOT: config/role_permissions.yaml (sync via API)
 * Key: module name
 * Value: array of roles that have access to this module
 */
export const MODULE_PERMISSIONS: Record<ModuleName, UserRole[]> = {
  // On Model / Flat Lay - dostępne tylko dla admin
  'on-model':      ['admin'],
  'flat-lay':      ['admin'],

  // Foto Sesja - tylko admin
  'easy-way':      ['admin'],

  // Stylista, Przymierzelnia, Makijaż, Fryzjer — osobne uprawnienia
  'stylista':      ['user', 'b2b-user', 'admin', 'mobile-users'],
  'dressing-room': ['user', 'b2b-user', 'admin', 'mobile-users'],
  'makeup':        ['user', 'b2b-user', 'admin', 'mobile-users'],
  'hairdresser':   ['user', 'b2b-user', 'admin', 'mobile-users'],

  // Video
  'video':         ['b2b-user', 'admin'],

  // Credits - dla wszystkich
  'credits':       ['user', 'b2b-user', 'admin', 'mobile-users'],

  // Admin panel
  'admin':         ['admin'],

  // Photo Tools
  'photo-tools':   ['b2b-user', 'admin'],
};

/**
 * Check if user has access to a module
 * @param userRole User's role
 * @param module Module name to check
 * @returns true if user has access, false otherwise
 */
export function hasModuleAccess(userRole: UserRole | undefined, module: ModuleName): boolean {
  if (!userRole) return false;

  const allowedRoles = MODULE_PERMISSIONS[module];
  return allowedRoles.includes(userRole);
}

/**
 * Get all modules accessible by a role
 * @param userRole User's role
 * @returns Array of module names the user can access
 */
export function getAccessibleModules(userRole: UserRole | undefined): ModuleName[] {
  if (!userRole) return [];

  return (Object.keys(MODULE_PERMISSIONS) as ModuleName[]).filter(module =>
    hasModuleAccess(userRole, module)
  );
}

/**
 * Check if role is admin
 * @param userRole User's role
 * @returns true if admin, false otherwise
 */
export function isAdminRole(userRole: UserRole | undefined): boolean {
  return userRole === 'admin';
}

/**
 * Check if role is b2b user
 * @param userRole User's role
 * @returns true if b2b-user, false otherwise
 */
export function isB2BRole(userRole: UserRole | undefined): boolean {
  return userRole === 'b2b-user';
}
